// Primary Time Study entry form: subject + duration required; enrichment optional.

import { useState, type FormEvent } from "react";
import {
  inputClass,
  mfgPrimaryBtnClass,
  ghostBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { minutesInputToSeconds } from "@/lib/manufacturing/timeStudyFormat";

export type TimeStudySubjectOption = {
  kind: "freeform" | "recipe" | "build_model";
  id?: string;
  label: string;
};

export type TimeStudyEntryFormValues = {
  subject_label: string;
  duration_seconds: number;
  entry_kind: "recipe" | "build_model" | "freeform";
  recipe_id: string | null;
  build_model_id: string | null;
  plant_id: string | null;
  unit_id: string | null;
  personnel_label: string | null;
  quantity: number | null;
  quantity_unit: string | null;
  notes: string | null;
};

type Props = {
  recipes: Array<{ id: string; name: string; code?: string }>;
  buildModels: Array<{ id: string; name: string; model_code?: string; flat_labor_rate_usd?: number }>;
  plants: Array<{ id: string; name: string; plant_code?: string }>;
  defaultPlantId?: string | null;
  busy?: boolean;
  onSubmit: (values: TimeStudyEntryFormValues) => Promise<void> | void;
};

export function TimeStudyEntryForm({
  recipes,
  buildModels,
  plants,
  defaultPlantId,
  busy,
  onSubmit,
}: Props) {
  const [subjectMode, setSubjectMode] = useState<"freeform" | "recipe" | "build_model">("freeform");
  const [subjectLabel, setSubjectLabel] = useState("");
  const [recipeId, setRecipeId] = useState("");
  const [buildModelId, setBuildModelId] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [personnel, setPersonnel] = useState("");
  const [plantId, setPlantId] = useState(defaultPlantId ?? "");
  const [unitSerial, setUnitSerial] = useState("");
  const [quantity, setQuantity] = useState("");
  const [quantityUnit, setQuantityUnit] = useState("");
  const [notes, setNotes] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const resolveSubject = (): {
    subject_label: string;
    entry_kind: "recipe" | "build_model" | "freeform";
    recipe_id: string | null;
    build_model_id: string | null;
  } | null => {
    if (subjectMode === "recipe") {
      const r = recipes.find((x) => x.id === recipeId);
      if (!r) return null;
      return {
        subject_label: r.name,
        entry_kind: "recipe",
        recipe_id: r.id,
        build_model_id: null,
      };
    }
    if (subjectMode === "build_model") {
      const m = buildModels.find((x) => x.id === buildModelId);
      if (!m) return null;
      return {
        subject_label: m.name,
        entry_kind: "build_model",
        recipe_id: null,
        build_model_id: m.id,
      };
    }
    const label = subjectLabel.trim();
    if (!label) return null;
    return {
      subject_label: label,
      entry_kind: "freeform",
      recipe_id: null,
      build_model_id: null,
    };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const subject = resolveSubject();
    const duration = minutesInputToSeconds(durationMinutes);
    if (!subject) {
      setLocalError("Subject is required (free-form label, recipe, or build model).");
      return;
    }
    if (duration == null) {
      setLocalError("Duration (minutes) is required and must be positive.");
      return;
    }
    const qtyRaw = quantity.trim() ? Number(quantity) : null;
    const serialNote = unitSerial.trim() ? `Serial: ${unitSerial.trim()}` : "";
    const notesCombined = [notes.trim(), serialNote].filter(Boolean).join("\n") || null;
    await onSubmit({
      ...subject,
      duration_seconds: duration,
      plant_id: plantId || null,
      unit_id: null,
      personnel_label: personnel.trim() || null,
      quantity: qtyRaw != null && Number.isFinite(qtyRaw) ? qtyRaw : null,
      quantity_unit: quantityUnit.trim() || null,
      notes: notesCombined,
    });
    setSubjectLabel("");
    setRecipeId("");
    setBuildModelId("");
    setDurationMinutes("");
    setPersonnel("");
    setUnitSerial("");
    setQuantity("");
    setQuantityUnit("");
    setNotes("");
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <div className="flex flex-wrap gap-2 text-xs">
        {(
          [
            ["freeform", "Free-form"],
            ["recipe", "Recipe"],
            ["build_model", "Build model"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`${ghostBtnClass} ${subjectMode === id ? "ring-2 ring-indigo-500" : ""}`}
            onClick={() => setSubjectMode(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {subjectMode === "freeform" ? (
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Subject
          <input
            className={`${inputClass} mt-1 w-full`}
            value={subjectLabel}
            onChange={(e) => setSubjectLabel(e.target.value)}
            placeholder="e.g. Unload flooring truck"
            disabled={busy}
          />
        </label>
      ) : null}

      {subjectMode === "recipe" ? (
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Recipe
          <select
            className={`${inputClass} mt-1 w-full`}
            value={recipeId}
            onChange={(e) => setRecipeId(e.target.value)}
            disabled={busy}
          >
            <option value="">Select recipe…</option>
            {recipes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code ? `${r.code} · ` : ""}
                {r.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {subjectMode === "build_model" ? (
        <label className="block text-sm text-gray-700 dark:text-zinc-300">
          Build model
          <select
            className={`${inputClass} mt-1 w-full`}
            value={buildModelId}
            onChange={(e) => setBuildModelId(e.target.value)}
            disabled={busy}
          >
            <option value="">Select build model…</option>
            {buildModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.model_code ? `${m.model_code} · ` : ""}
                {m.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="block text-sm text-gray-700 dark:text-zinc-300">
        Duration (minutes)
        <input
          className={`${inputClass} mt-1 w-full max-w-[12rem]`}
          type="number"
          min={0.1}
          step="any"
          value={durationMinutes}
          onChange={(e) => setDurationMinutes(e.target.value)}
          placeholder="45"
          disabled={busy}
        />
      </label>

      <button
        type="button"
        className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
        onClick={() => setShowMore((v) => !v)}
      >
        {showMore ? "Hide details" : "More details (optional)"}
      </button>

      {showMore ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Personnel
            <input
              className={`${inputClass} mt-1 w-full`}
              value={personnel}
              onChange={(e) => setPersonnel(e.target.value)}
              placeholder="Who did the work"
              disabled={busy}
            />
          </label>
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Plant
            <select
              className={`${inputClass} mt-1 w-full`}
              value={plantId}
              onChange={(e) => setPlantId(e.target.value)}
              disabled={busy}
            >
              <option value="">(none)</option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.plant_code ? `${p.plant_code} · ` : ""}
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Serial / unit note
            <input
              className={`${inputClass} mt-1 w-full`}
              value={unitSerial}
              onChange={(e) => setUnitSerial(e.target.value)}
              placeholder="Optional serial (notes for now)"
              disabled={busy}
            />
          </label>
          <div className="flex gap-2">
            <label className="block flex-1 text-sm text-gray-700 dark:text-zinc-300">
              Quantity
              <input
                className={`${inputClass} mt-1 w-full`}
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="block flex-1 text-sm text-gray-700 dark:text-zinc-300">
              Unit
              <input
                className={`${inputClass} mt-1 w-full`}
                value={quantityUnit}
                onChange={(e) => setQuantityUnit(e.target.value)}
                placeholder="sq ft"
                disabled={busy}
              />
            </label>
          </div>
          <label className="block text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
            Notes
            <textarea
              className={`${inputClass} mt-1 w-full`}
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>
      ) : null}

      {localError ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {localError}
        </p>
      ) : null}

      <button type="submit" className={mfgPrimaryBtnClass} disabled={busy}>
        {busy ? "Saving…" : "Add observation"}
      </button>
    </form>
  );
}
